// Payment Tracking convergence — TanStack Query hooks for the loan tab's
// Payment System v3 servicing surface. Mirrors the usePortfolioAdmin
// conventions: stable key factory; mutations invalidate the touched
// queries; toasts + ApiError rendering happen in the calling component.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  servicingApi,
  type ClearRecipientOverrideRequest,
  type CreateLoanServicingFeeConfigRequest,
  type RecordPaymentExplainRequest,
  type RecordServicingPaymentRequest,
  type ServicingReviewDto,
  type SetRecipientOverrideRequest,
} from "@/api/portfolioAdmin/servicingApi";
import { invalidateLoanHealth, invalidateLoanSummaryAndHealth } from "./invalidation";

export const servicingKeys = {
  ledger: (loanId: string) =>
    ["portfolio-admin", "loan", loanId, "servicing-ledger"] as const,
  allocation: (paymentId: string) =>
    ["portfolio-admin", "servicing-payment", paymentId, "allocation"] as const,
  feeConfigs: (loanId: string) =>
    ["portfolio-admin", "loan", loanId, "servicing-fee-configs"] as const,
  // Distribution Gate (B3 payout gate) — the B3 review queue.
  pendingReview: () =>
    ["portfolio-admin", "servicing-payments", "pending-review"] as const,
  // Distribution Gate PR-2 — the per-line review payload for one held run.
  review: (paymentId: string) =>
    ["portfolio-admin", "servicing-payment", paymentId, "review"] as const,
};

export function useServicingLedger(loanId: string | undefined) {
  return useQuery({
    queryKey: servicingKeys.ledger(loanId ?? ""),
    queryFn: () => servicingApi.ledger(loanId!),
    enabled: !!loanId,
  });
}

export function useServicingAllocation(paymentId: string | undefined) {
  return useQuery({
    queryKey: servicingKeys.allocation(paymentId ?? ""),
    queryFn: () => servicingApi.allocation(paymentId!),
    enabled: !!paymentId,
  });
}

export function useRecordServicingPayment(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RecordServicingPaymentRequest) =>
      servicingApi.recordPayment(loanId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: servicingKeys.ledger(loanId) });
      // The Overview header derives days-delinquent from the same ledger.
      void qc.invalidateQueries({ queryKey: ["portfolio-admin", "loan", loanId] });
    },
  });
}

/** "Ask AI" payment coach for the Record Payment wizard — a POST, but purely
 *  a read (mirrors useExplainWaterfallPriority). */
export function useExplainRecordPayment() {
  return useMutation({
    // no-invalidation: read-only AI explanation; no cached reads change
    mutationFn: (args: { loanId: string; body: RecordPaymentExplainRequest }) =>
      servicingApi.explainRecordPayment(args.loanId, args.body),
  });
}

/** Distribution Gate (B3 payout gate) — the B3 review queue of recorded
 *  servicing runs held pending_review. */
export function useServicingPendingReview() {
  return useQuery({
    queryKey: servicingKeys.pendingReview(),
    queryFn: () => servicingApi.pendingReview(),
  });
}

/** B3 approves a held run: it goes live (recognition + distribution released).
 *  Invalidates the queue and the loan's ledger + summary/health (the run now
 *  counts as a receipt and its distribution can materialize). */
export function useApproveServicingReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { paymentId: string; loanId: string }) =>
      servicingApi.approveReview(vars.paymentId),
    onSuccess: (_res, vars) => {
      void qc.invalidateQueries({ queryKey: servicingKeys.pendingReview() });
      void qc.invalidateQueries({ queryKey: servicingKeys.ledger(vars.loanId) });
      void invalidateLoanSummaryAndHealth(qc, vars.loanId);
    },
  });
}

/** B3 rejects a held run at review: it is unwound. Same invalidations as
 *  approve (the run leaves the queue and the loan ledger changes). */
export function useRejectServicingReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { paymentId: string; loanId: string; reason: string }) =>
      servicingApi.rejectReview(vars.paymentId, vars.reason),
    onSuccess: (_res, vars) => {
      void qc.invalidateQueries({ queryKey: servicingKeys.pendingReview() });
      void qc.invalidateQueries({ queryKey: servicingKeys.ledger(vars.loanId) });
      void invalidateLoanSummaryAndHealth(qc, vars.loanId);
    },
  });
}

/** Distribution Gate PR-2 — the per-line B3 review payload for a held run:
 *  live allocation lines + rule checks + recipient corrections. */
export function useServicingReview(paymentId: string | undefined) {
  return useQuery({
    queryKey: servicingKeys.review(paymentId ?? ""),
    queryFn: () => servicingApi.review(paymentId!),
    enabled: !!paymentId,
  });
}

/** B3 corrects an allocation line's recipient (identity-only, D-13). The
 *  response is the rebuilt review payload; the draft's ledger also changed. */
export function useSetRecipientOverride(paymentId: string, loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SetRecipientOverrideRequest) =>
      servicingApi.setRecipientOverride(paymentId, body),
    onSuccess: (res: ServicingReviewDto) => {
      qc.setQueryData(servicingKeys.review(paymentId), res);
      void qc.invalidateQueries({ queryKey: servicingKeys.ledger(loanId) });
      void invalidateLoanSummaryAndHealth(qc, loanId);
    },
  });
}

/** B3 clears a recipient correction (revert to the resolver). */
export function useClearRecipientOverride(paymentId: string, loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ClearRecipientOverrideRequest) =>
      servicingApi.clearRecipientOverride(paymentId, body),
    onSuccess: (res: ServicingReviewDto) => {
      qc.setQueryData(servicingKeys.review(paymentId), res);
      void qc.invalidateQueries({ queryKey: servicingKeys.ledger(loanId) });
      void invalidateLoanSummaryAndHealth(qc, loanId);
    },
  });
}

/** B3 recalculates a held run's draft after fixing an underlying datum. */
export function useRecalculateReview(paymentId: string, loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => servicingApi.recalculateReview(paymentId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: servicingKeys.review(paymentId) });
      void qc.invalidateQueries({ queryKey: servicingKeys.ledger(loanId) });
      void invalidateLoanSummaryAndHealth(qc, loanId);
    },
  });
}

export function useServicingFeeConfigs(loanId: string | undefined) {
  return useQuery({
    queryKey: servicingKeys.feeConfigs(loanId ?? ""),
    queryFn: () => servicingApi.feeConfigs(loanId!),
    enabled: !!loanId,
  });
}

export function useCreateServicingFeeConfig(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateLoanServicingFeeConfigRequest) =>
      servicingApi.createFeeConfig(loanId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: servicingKeys.feeConfigs(loanId) });
      void qc.invalidateQueries({ queryKey: servicingKeys.ledger(loanId) });
      // Fee sizing changes the ledger-derived payment severity.
      void invalidateLoanHealth(qc, loanId);
    },
  });
}
